import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { EventBusy } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import leaveService, { LeaveType } from '../../services/leaveService';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';
import ColorSwatchPicker, { swatchAt } from '../common/ColorSwatchPicker';

/** The leave code a name suggests: uppercase letters joined by underscores, at most 20. */
const codeFor = (name: string) =>
  name.toUpperCase().replace(/[^A-Z]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').substring(0, 20);

const CODE = /^[A-Za-z_]+$/;

const codeError = (code: string) => {
  if (!code) return 'A leave type needs a code';
  if (!CODE.test(code)) return 'Use letters and underscores only';
  return null;
};

const LeaveTypesSettings: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_leave_types');

  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [form, setForm] = useState({ name: '', code: '', description: '', isPaid: true, color: swatchAt(0) });
  // The code follows the name only while it is still the one the name suggested.
  const [codeFollowsName, setCodeFollowsName] = useState(false);
  const [saving, setSaving] = useState(false);
  // Leave types created since the last load, so each one in a batch gets its own colour.
  const addedSinceLoad = useRef(0);

  const load = useCallback(async () => {
    try {
      setLeaveTypes(await leaveService.getLeaveTypes());
      addedSinceLoad.current = 0;
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load leave types', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const create = async (name: string) => {
    const code = codeFor(name);
    if (!code) throw new Error('A name needs letters to make a leave code');
    return leaveService.createLeaveType({
      name,
      code,
      isPaid: true,
      color: swatchAt(leaveTypes.length + addedSinceLoad.current++),
    });
  };

  const edit = (t: LeaveType) => {
    setEditing(t);
    setForm({ name: t.name, code: t.code, description: t.description ?? '', isPaid: t.isPaid, color: t.color || swatchAt(0) });
    setCodeFollowsName(t.code === codeFor(t.name));
  };

  const save = async () => {
    if (!editing || !form.name.trim() || codeError(form.code)) return;
    setSaving(true);
    try {
      await leaveService.updateLeaveType(editing._id, { ...form, name: form.name.trim(), code: form.code.toUpperCase() });
      enqueueSnackbar('Leave type saved', { variant: 'success' });
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this leave type', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formCodeError = codeError(form.code);

  return (
    <>
      <SettingsList
        rows={leaveTypes}
        loading={loading}
        rowKey={t => t._id}
        name={t => t.name}
        subtitle={t => (
          <>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{t.code}</Box>
            {' · '}
            {t.isPaid ? 'Paid' : 'Unpaid'}
            {t.description ? ` · ${t.description}` : ''}
          </>
        )}
        leading={t => <SettingsTile color={t.color}><EventBusy /></SettingsTile>}
        searchText={t => `${t.name} ${t.code} ${t.description ?? ''}`}
        canWrite={writable}
        onEdit={edit}
        remove={{
          run: t => leaveService.deleteLeaveType(t._id),
          noun: 'leave type',
          consequence: () => 'It will no longer be offered when applying for leave. Leave already requested or taken keeps its type.',
        }}
        quickAdd={{
          noun: { one: 'leave type', many: 'leave types' },
          placeholder: 'Add leave types, e.g. Casual, Sick, Annual',
          create,
          presets: [
            { label: 'Common', names: ['Casual', 'Sick', 'Annual'] },
            { label: 'Special', names: ['Maternity', 'Paternity', 'Hajj', 'Unpaid'] },
          ],
        }}
        reload={load}
        empty={{
          icon: <EventBusy />,
          title: 'No leave types yet',
          description: 'Leave types are what staff apply for. Add them above — several at once; each is paid until you edit it.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title="Edit leave type"
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveDisabled={!form.name.trim() || !!formCodeError}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => {
            const name = e.target.value;
            setForm(f => ({ ...f, name, code: codeFollowsName ? codeFor(name) : f.code }));
          }}
          error={!form.name.trim()}
          helperText={!form.name.trim() ? 'A leave type needs a name' : ' '}
          inputProps={{ maxLength: 100 }}
          fullWidth
        />
        <TextField
          label="Code"
          value={form.code}
          onChange={e => {
            setCodeFollowsName(false);
            setForm(f => ({ ...f, code: e.target.value }));
          }}
          error={!!formCodeError}
          helperText={formCodeError ?? 'A short unique code, e.g. SICK or CASUAL'}
          inputProps={{ maxLength: 20, style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Optional"
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={2}
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={form.isPaid} onChange={e => setForm(f => ({ ...f, isPaid: e.target.checked }))} />}
          label={form.isPaid ? 'Paid leave' : 'Unpaid leave'}
        />
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">Colour</Typography>
          <ColorSwatchPicker value={form.color} onChange={color => setForm(f => ({ ...f, color }))} />
        </Stack>
      </SettingsDialog>
    </>
  );
};

export default LeaveTypesSettings;
